/*
 * Copyright (c) CIRROM 2026.
 */

import { darken, Theme } from "@mui/material/styles";
import { CRM_SCORE_BANDS } from "../../utils/utils";

/*
 * The one ramp, made safe to draw with.
 *
 * CRM_SCORE_BANDS is authored as FILL colour: it backs gauge arcs, score
 * chips and swatches, where nothing is read through it and the shape itself
 * carries the signal. Used raw as a foreground - an icon stroke on paper -
 * the lighter bands fall under the 3:1 a non-text control owes: Outstanding
 * measures 2.42:1 on white and Average about 2:1.
 *
 * So a surface drawing WITH the ramp derives from the same band rather than
 * authoring a second green or a second amber. Light mode darkens until the
 * control is legible; dark mode leaves the band alone, because against the
 * Night Field these values already clear 5:1 and darkening would undo that.
 * One ramp, one definition, two renderings.
 */

/** Raw band value. Correct for fills and swatches; see rampIconColor for strokes. */
export const rampBandColor = (label: string): string =>
  CRM_SCORE_BANDS.find((band) => band.label === label)?.color;

/**
 * The same band, legible as an icon or other non-text control.
 * Pass straight to `sx` - MUI calls it with the active theme.
 */
export const rampIconColor =
  (label: string) =>
  (theme: Theme): string => {
    const base = rampBandColor(label);
    if (!base) {
      return theme.palette.text.secondary;
    }
    return theme.palette.mode === "dark" ? base : darken(base, 0.3);
  };
